/* A copy of this person's data on the device (IndexedDB), so the app opens instantly — also on a phone with a weak
   signal — and then catches up with the server. One copy per login; deleted on sign-out. The queue of changes not yet
   saved lives in localStorage (small, and survives closing the app); at sign-out it moves to that login's Not saved list. */
import type {Op} from './lib/sync.ts';

const DB = 'october-time-tracker', STORE = 'cache', VERSION = 1;
export const CACHE_FORMAT = 5;   // bump when the shape changes: an old copy is then ignored and everything is loaded fresh

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, VERSION);
    r.onupgradeneeded = () => { r.result.createObjectStore(STORE); };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await open();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const t = db.transaction(STORE, mode), req = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(req ? req.result : undefined);
      t.onerror = () => reject(t.error); t.onabort = () => reject(t.error);
    });
  } finally { db.close(); }
}
export async function readCache<T>(userId: string): Promise<T | null> {
  try { const v: any = await tx('readonly', s => s.get(userId)); return v && v.format === CACHE_FORMAT ? v as T : null; } catch { return null; }
}
export async function writeCache(userId: string, value: object): Promise<void> {
  try { await tx('readwrite', s => s.put({...value, format: CACHE_FORMAT}, userId)); } catch (e) { console.warn('cache not saved', e); }
}
export async function clearCache(): Promise<void> {
  try { await tx('readwrite', s => s.clear()); } catch { /* nothing cached */ }
}

const OUTBOX = (userId: string) => 'ot_outbox_' + userId;
export function readOutbox(userId: string): Op[] { try { return JSON.parse(localStorage.getItem(OUTBOX(userId)) || '[]'); } catch { return []; } }
export function writeOutbox(userId: string, ops: Op[]): void { try { localStorage.setItem(OUTBOX(userId), JSON.stringify(ops)); } catch { /* private mode: kept in memory only */ } }
export function clearOutbox(userId: string): void { try { localStorage.removeItem(OUTBOX(userId)); } catch { /* ignore */ } }

/* "Not saved" (owner decision 2026-10-13): changes the database refused, kept per login until the person retries, fixes or
   discards them. Deliberately NOT removed on sign-out: a time record is never thrown away silently. At sign-out the queue's
   waiting changes move here too (syncer.stop) and go back into the queue at the same login's next sign-in. */
const NOT_SAVED = (userId: string) => 'ot_notsaved_' + userId;
export function readNotSaved<T>(userId: string): T[] { try { return JSON.parse(localStorage.getItem(NOT_SAVED(userId)) || '[]'); } catch { return []; } }
export function writeNotSaved(userId: string, items: unknown[]): void {
  try { items.length ? localStorage.setItem(NOT_SAVED(userId), JSON.stringify(items)) : localStorage.removeItem(NOT_SAVED(userId)); } catch { /* private mode: kept in memory only */ }
}
